import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import {
  PageContainer,
  HeaderContainer,
  Title,
  FilterBar,
  Field,
  Label,
  StyledSelect,
  StyledInput,
  AddUserButton,
} from "./TablaActivos.styles";
import { envasesService, CatalogosEnvase } from "../../services/envasesService";
import { productosService } from "../../services/productosService";
import { CreaEnvaseDto } from "../../dtos/Envase";
import { Productos } from "../../dtos/Productos";
import { ApiError } from "../../services/apiError";
import ProductoAutocomplete from "./ProductoAutocomplete";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: "Ocurrió un error inesperado." };
};

const emptyForm: CreaEnvaseDto = {
  numeroSerie: "",
  pesoTara: null,
  evidencia: "",
  codigoProducto: null,
  fabricanteId: "",
  modeloId: "",
  idTipoEnvase: null,
};

const RegistroEnvase: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreaEnvaseDto>({ ...emptyForm });
  const [catalogos, setCatalogos] = useState<CatalogosEnvase | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  const [producto, setProducto] = useState<Productos | null>(null);
  const [loadingProducto, setLoadingProducto] = useState(false);

  useEffect(() => {
    envasesService
      .loadCatalogosEnvase()
      .then(setCatalogos)
      .catch((err) => notify(handleApiError(err).message, "error", 3000))
      .finally(() => setLoadingCatalogos(false));
  }, []);

  const modelosFiltrados =
    catalogos?.modelos.filter(
      (m) => !form.fabricanteId || m.fabricanteId === form.fabricanteId,
    ) ?? [];

  const handleProductoChange = async (codigo: number | null) => {
    setField("codigoProducto", codigo);
    if (!codigo) {
      setProducto(null);
      return;
    }
    setLoadingProducto(true);
    try {
      const prod = await productosService.getByCodigo(codigo);
      setProducto(prod ?? null);
    } catch (err) {
      setProducto(null);
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoadingProducto(false);
    }
  };

  const productoCompleto =
    !!producto &&
    !!producto.codigoCuerpo &&
    !!producto.codigoCapuchon &&
    !!producto.codigoCuello &&
    !!producto.codigoEstarcido &&
    !!producto.codigoOjiva &&
    producto.idValvula != null;

  const handleSubmit = async () => {
    if (!form.numeroSerie?.trim()) {
      notify("El número de serie es obligatorio.", "warning", 2500);
      return;
    }
    if (!form.fabricanteId || !form.modeloId || !form.idTipoEnvase) {
      notify(
        "Fabricante, modelo y tipo de envase son obligatorios.",
        "warning",
        2500,
      );
      return;
    }
    if (producto && !productoCompleto) {
      notify(
        "El producto seleccionado no tiene la configuración de catálogos completa.",
        "warning",
        3000,
      );
      return;
    }
    setLoading(true);
    try {
      // Los catálogos NO se envían: el backend los copia (snapshot) desde el producto.
      const payload: CreaEnvaseDto = {
        numeroSerie: form.numeroSerie.trim(),
        pesoTara: form.pesoTara ?? null,
        evidencia: form.evidencia || null,
        codigoProducto: form.codigoProducto || null,
        fabricanteId: form.fabricanteId || null,
        modeloId: form.modeloId || null,
        idTipoEnvase: form.idTipoEnvase || null,
      };
      await envasesService.createEnvase(payload);
      notify("Envase registrado correctamente.", "success", 2500);
      navigate("/consulta-activos");
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  const setField = (field: keyof CreaEnvaseDto, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loadingCatalogos || !catalogos) {
    return (
      <PageContainer>
        <Title>Cargando catálogos...</Title>
      </PageContainer>
    );
  }

  const catalogoItem = (
    label: string,
    codigo?: string | null,
    extra?: string | null,
  ) => (
    <Field>
      <Label>{label}</Label>
      <StyledInput
        value={
          codigo ? (extra ? `${codigo} - ${extra}` : codigo) : "—"
        }
        readOnly
        disabled
      />
    </Field>
  );

  return (
    <PageContainer>
      <div style={{ marginBottom: 15 }}>
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate("/consulta-activos")}
        />
      </div>
      <HeaderContainer>
        <Title>Registrar envase</Title>
      </HeaderContainer>

      <FilterBar style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        <Field>
          <Label>Número de serie *</Label>
          <StyledInput
            value={form.numeroSerie ?? ""}
            onChange={(e) => setField("numeroSerie", e.target.value)}
          />
        </Field>
        <Field>
          <Label>Peso tara (kg)</Label>
          <StyledInput
            type="number"
            value={form.pesoTara ?? ""}
            onChange={(e) =>
              setField("pesoTara", e.target.value === "" ? null : Number(e.target.value))
            }
          />
        </Field>
        <Field>
          <Label>Fabricante *</Label>
          <StyledSelect
            value={form.fabricanteId ?? ""}
            onChange={(e) => {
              setField("fabricanteId", e.target.value);
              setField("modeloId", "");
            }}
          >
            <option value="">Selecciona...</option>
            {catalogos.fabricantes.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nombre}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Modelo *</Label>
          <StyledSelect
            value={form.modeloId ?? ""}
            onChange={(e) => setField("modeloId", e.target.value)}
          >
            <option value="">Selecciona...</option>
            {modelosFiltrados.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Tipo de envase *</Label>
          <StyledSelect
            value={form.idTipoEnvase ?? ""}
            onChange={(e) =>
              setField(
                "idTipoEnvase",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
          >
            <option value="">Selecciona...</option>
            {catalogos.tiposEnvase.map((t) => (
              <option key={t.idTipoEnvase} value={t.idTipoEnvase}>
                {t.tipoDeEnvase || t.descripcion}
              </option>
            ))}
          </StyledSelect>
        </Field>
        <Field>
          <Label>Código producto (opcional)</Label>
          <ProductoAutocomplete
            value={form.codigoProducto ?? null}
            onChange={handleProductoChange}
          />
        </Field>
        <Field style={{ gridColumn: "1 / -1" }}>
          <Label>Evidencia (URL)</Label>
          <StyledInput
            value={form.evidencia ?? ""}
            onChange={(e) => setField("evidencia", e.target.value)}
          />
        </Field>

        <div style={{ gridColumn: "1 / -1" }}>
          <h4 style={{ margin: "8px 0", color: "#42526E" }}>
            Catálogos del producto (solo lectura)
          </h4>
          {!form.codigoProducto && (
            <p style={{ fontSize: 13, color: "#b45309", margin: "0 0 8px" }}>
              Sin producto: el envase se registrará con catálogos pendientes
              (podrá completarse después asignando un producto).
            </p>
          )}
          {!!form.codigoProducto && loadingProducto && (
            <p style={{ fontSize: 13, color: "#6B778C" }}>Cargando producto...</p>
          )}
          {!!form.codigoProducto && !loadingProducto && producto && !productoCompleto && (
            <p style={{ fontSize: 13, color: "#dc2626", margin: "0 0 8px" }}>
              El producto no tiene la configuración de catálogos completa.
            </p>
          )}
        </div>

        {producto && (
          <>
            {catalogoItem("Cuerpo", producto.codigoCuerpo, producto.colorCuerpo)}
            {catalogoItem("Capuchón", producto.codigoCapuchon, producto.colorCapuchon)}
            {catalogoItem("Cuello", producto.codigoCuello, producto.colorCuello)}
            {catalogoItem("Estarcido", producto.codigoEstarcido, producto.colorEstarcido)}
            {catalogoItem("Ojiva", producto.codigoOjiva, producto.colorOjiva)}
            {catalogoItem(
              "Válvula",
              producto.idValvula != null ? String(producto.idValvula) : null,
              producto.valvula,
            )}
          </>
        )}

        <AddUserButton onClick={handleSubmit} disabled={loading}>
          {loading ? "Guardando..." : "Registrar envase"}
        </AddUserButton>
      </FilterBar>
    </PageContainer>
  );
};

export default RegistroEnvase;
